import { InputHTMLAttributes, forwardRef, useEffect } from 'react';
import { cn } from '../utils/cn';

export interface CheckboxProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string;
  error?: string;
  indeterminate?: boolean;
}

/**
 * Checkbox - Accessible checkbox component with indeterminate state support
 * 
 * Required CSS classes:
 * - .checkbox-wrapper - Wrapper container
 * - .checkbox - Base checkbox input styles
 * - .checkbox-label - Label styles
 * - .checkbox-error - Error state styles
 * - .checkbox-error-message - Error message styles
 * 
 * Accessibility:
 * - Keyboard: Tab (focus), Space (toggle)
 * - Screen reader: Announces label, checked state, and error
 * - Focus: Visible focus indicator
 * 
 * @example
 * <Checkbox label="I agree to the terms" checked={checked} onChange={handleChange} />
 * 
 * @example
 * <Checkbox label="Select all" indeterminate={indeterminate} />
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ className, label, error, id, indeterminate, checked, ...props }, ref) => {
    const checkboxId = id || props.name;

    return (
      <div className="checkbox-wrapper">
        <input
          ref={ref}
          type="checkbox"
          id={checkboxId}
          className={cn('checkbox', error && 'checkbox-error', className)}
          aria-invalid={!!error}
          aria-describedby={error ? `${checkboxId}-error` : undefined}
          checked={checked}
          {...props}
        />
        {label && (
          <label htmlFor={checkboxId} className="checkbox-label">
            {label}
          </label>
        )}
        {error && (
          <p id={`${checkboxId}-error`} className="checkbox-error-message" role="alert">
            {error}
          </p>
        )}
      </div>
    );
  }
);

Checkbox.displayName = 'Checkbox';

// Set indeterminate state via ref
export function useCheckboxIndeterminate(
  ref: React.RefObject<HTMLInputElement>,
  indeterminate: boolean
) {
  useEffect(() => {
    if (ref.current) {
      ref.current.indeterminate = indeterminate;
    }
  }, [ref, indeterminate]);
}
